import React, { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import {
    subscribeToNotifications,
    markNotificationAsRead,
    markAllAsRead
} from '../../services/business/notificationService';
import { getDocks } from '../../services/business/dockService';
import { NOTIFICATION_TYPES, NOTIFICATION_LABELS } from '../../constants/notifications';
import { STATUS_LABELS } from '../../constants/statuses';
import Combobox from '../../components/shared/Combobox';
import HistoryLogModal from '../../components/shared/HistoryLogModal';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

dayjs.locale('tr');

/**
 * Premium Notifications Page
 * Redesigned to match the 'Dock Status' aesthetic with high-end glassmorphism and LED effects.
 */
const Notifications = ({ userData }) => {
    const { addToast } = useToast();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('ALL');
    const [dateRange, setDateRange] = useState('TODAY');
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

    // For History Modal
    const [selectedReservationId, setSelectedReservationId] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [docks, setDocks] = useState([]);

    // Load Docks for name mapping
    useEffect(() => {
        const loadDocks = async () => {
            try {
                const dockList = await getDocks(userData.tenantId);
                setDocks(dockList);
            } catch (error) {
                console.error('Error loading docks:', error);
            }
        };
        if (userData?.tenantId) loadDocks();
    }, [userData?.tenantId]);

    // Subscribe to notifications
    useEffect(() => {
        if (!userData?.tenantId) return;

        setLoading(true);
        const unsubscribe = subscribeToNotifications(
            userData.tenantId,
            userData.uid,
            (newNotifications) => {
                setNotifications(newNotifications);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [userData]);

    const handleNotificationClick = async (notification) => {
        if (!notification.read) {
            try {
                await markNotificationAsRead(notification.id);
            } catch (error) {
                console.error('Error marking notification as read:', error);
            }
        }

        if (notification.reservationId) {
            setSelectedReservationId(notification.reservationId);
            setIsHistoryOpen(true);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllAsRead(userData.tenantId, userData.uid);
            addToast('Tüm bildirimler okundu olarak işaretlendi.', 'success');
        } catch (error) {
            addToast('Hata oluştu.', 'error');
        }
    };

    const formatValue = (value, type) => {
        if (value === null || value === undefined || value === '') return '---';

        switch (type) {
            case NOTIFICATION_TYPES.STATUS_CHANGE:
                return STATUS_LABELS[value] || value;
            case NOTIFICATION_TYPES.TIME_CHANGE:
                return value;
            case NOTIFICATION_TYPES.RAMP_CHANGE:
                if (!value) return 'BEKLEME LİSTESİ';
                const dock = docks.find(d => d.id === value);
                return dock ? dock.name : 'BİLİNMEYEN RAMPA';
            default:
                return String(value);
        }
    };

    const getFieldLabel = (type) => {
        switch (type) {
            case NOTIFICATION_TYPES.STATUS_CHANGE: return 'STATÜ';
            case NOTIFICATION_TYPES.TIME_CHANGE: return 'SAAT';
            case NOTIFICATION_TYPES.PLATE_CHANGE: return 'PLAKA';
            case NOTIFICATION_TYPES.DRIVER_CHANGE: return 'SÜRÜCÜ';
            case NOTIFICATION_TYPES.RAMP_CHANGE: return 'RAMPA';
            default: return 'GÜNCELLEME';
        }
    };

    const filteredNotifications = notifications.filter(notif => {
        // Type Filter
        if (filter !== 'ALL' && notif.type !== filter) return false;

        // Date Filter
        const notifDate = dayjs(notif.timestamp);
        const start = dayjs(startDate).startOf('day');
        const end = dayjs(endDate).endOf('day');

        return notifDate.isAfter(start) && notifDate.isBefore(end);
    });

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="flex flex-col gap-lg p-lg animate-fadeIn" style={{ maxWidth: '1400px', margin: '0 auto', height: '100%', minHeight: 'calc(100vh - 100px)' }}>

            {/* Header Section */}
            <div className="flex items-center justify-between">
                <div className="flex flex-col">
                    <h1 className="text-3xl font-black tracking-tighter m-0" style={{ color: 'var(--text-primary)' }}>
                        BİLDİRİMLER
                        {unreadCount > 0 && (
                            <span className="ml-4 px-3 py-1 bg-primary text-white text-sm rounded-full shadow-[0_0_15px_rgba(59,130,246,0.5)]">
                                {unreadCount} YENİ
                            </span>
                        )}
                    </h1>
                    <p className="opacity-60 text-sm mt-1 uppercase tracking-widest font-bold" style={{ color: 'var(--text-secondary)' }}>Gerçek zamanlı rezervasyon güncellemeleri</p>
                </div>

                <button
                    onClick={handleMarkAllRead}
                    disabled={unreadCount === 0}
                    className="btn btn-secondary flex items-center gap-2 group"
                >
                    <span className="material-icons text-sm group-hover:rotate-12 transition-transform">done_all</span>
                    TÜMÜNÜ OKUNDU YAP
                </button>
            </div>

            {/* Filter Toolbar - Modern LED Style - Side by Side & Left Aligned */}
            <div className="card-premium glow-theme p-md flex flex-row items-center justify-start gap-lg">
                <div className="w-[300px]">
                    <Combobox
                        items={[
                            { id: 'ALL', label: '📢 Tüm Bildirimler' },
                            { id: NOTIFICATION_TYPES.TIME_CHANGE, label: '🕒 ' + NOTIFICATION_LABELS[NOTIFICATION_TYPES.TIME_CHANGE] },
                            { id: NOTIFICATION_TYPES.STATUS_CHANGE, label: '🚩 ' + NOTIFICATION_LABELS[NOTIFICATION_TYPES.STATUS_CHANGE] },
                            { id: NOTIFICATION_TYPES.PLATE_CHANGE, label: '🚛 ' + NOTIFICATION_LABELS[NOTIFICATION_TYPES.PLATE_CHANGE] },
                            { id: NOTIFICATION_TYPES.DRIVER_CHANGE, label: '👤 ' + NOTIFICATION_LABELS[NOTIFICATION_TYPES.DRIVER_CHANGE] },
                            { id: NOTIFICATION_TYPES.RAMP_CHANGE, label: '🏠 ' + NOTIFICATION_LABELS[NOTIFICATION_TYPES.RAMP_CHANGE] },
                        ]}
                        value={filter}
                        onChange={(item) => setFilter(item.id)}
                        className="w-full"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                </div>

                <div className="flex items-center gap-md">
                    <div className="w-[160px] relative">
                        <input
                            type="date"
                            className="input text-xs p-3 bg-white/5 border-white/10 rounded-xl w-full"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <span className="text-secondary/30 font-black">/</span>
                    <div className="w-[160px] relative">
                        <input
                            type="date"
                            className="input text-xs p-3 bg-white/5 border-white/10 rounded-xl w-full"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Notifications List Container */}
            <div
                className="flex-1 overflow-hidden flex flex-col gap-md"
                style={{ minHeight: '500px' }}
            >
                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-md">
                        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                        <span className="text-secondary font-bold tracking-widest text-xs uppercase animate-pulse">Yükleniyor...</span>
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center opacity-30 gap-md">
                        <span className="material-icons text-6xl">notifications_none</span>
                        <span className="text-sm font-black uppercase tracking-widest">Kayıt Bulunamadı</span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 overflow-y-auto custom-scrollbar pr-2 h-full">
                        {filteredNotifications.map(notif => (
                            <div
                                key={notif.id}
                                onClick={() => handleNotificationClick(notif)}
                                className={`card-premium transition-all duration-300 hover:scale-[1.005] hover:shadow-2xl cursor-pointer group ${notif.read ? 'opacity-80 grayscale-[0.2]' : 'glow-theme'}`}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2.5rem',
                                    background: 'var(--bg-card)',
                                    backdropFilter: 'blur(10px)',
                                    border: notif.read ? '1px solid var(--border-color)' : '2px solid var(--primary-color)',
                                    boxShadow: notif.read ? 'none' : '0 4px 20px rgba(var(--card-glow-rgb), 0.2)',
                                    borderRadius: '20px',
                                    position: 'relative',
                                    overflow: 'visible'
                                }}
                            >
                                {/* Left: Premium Type Badge (Vibrant Glass Style) */}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.5rem 1.5rem',
                                        borderRadius: '50px',
                                        background: notif.type === NOTIFICATION_TYPES.STATUS_CHANGE
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : notif.type === NOTIFICATION_TYPES.TIME_CHANGE
                                                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                                : notif.type === NOTIFICATION_TYPES.RAMP_CHANGE
                                                    ? 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'
                                                    : notif.type === NOTIFICATION_TYPES.PLATE_CHANGE
                                                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                                        : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                        opacity: notif.read ? 0.75 : 1, // Increased opacity for read items to keep them distinct
                                        filter: notif.read ? 'grayscale(30%)' : 'none', // Subtle grayscale for read
                                        border: '2px solid rgba(255,255,255,0.3)',
                                        boxShadow: 'none',
                                        minWidth: '150px',
                                        height: '36px',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: '#fff',
                                            boxShadow: '0 0 12px white',
                                            animation: !notif.read ? 'pulse-white 2s infinite' : 'none'
                                        }}
                                    />
                                    <span
                                        className="text-white font-black text-xs uppercase tracking-[0.15em] text-shadow-md"
                                        style={{ color: '#FFFFFF' }}
                                    >
                                        {getFieldLabel(notif.type)}
                                    </span>
                                </div>

                                {/* Main Content: Grid for perfect alignment */}
                                <div
                                    className="flex-1"
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '150px 180px 40px 180px 140px 180px 1fr',
                                        alignItems: 'center',
                                        gap: '1.5rem'
                                    }}
                                >
                                    {/* Plate Info */}
                                    <div className="flex flex-col">
                                        <h3 className="text-xl font-black m-0 tracking-tighter uppercase" style={{ color: 'var(--text-primary)' }}>
                                            {notif.plateNumber || 'PLAKA BELİRSİZ'}
                                        </h3>
                                        <div className="flex items-center gap-1.5 opacity-40">
                                            <span className="material-icons text-[12px]">schedule</span>
                                            <span className="text-[10px] font-black uppercase">{dayjs(notif.timestamp).format('HH:mm')} • {dayjs(notif.timestamp).format('DD MMM')}</span>
                                        </div>
                                    </div>

                                    {/* Comparison View (Old Value) */}
                                    <div
                                        className="flex items-center justify-center border border-white/20"
                                        style={{
                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            boxShadow: '0 0 15px rgba(239, 68, 68, 0.6), inset 0 2px 4px rgba(255,255,255,0.2)',
                                            borderRadius: '50px',
                                            padding: '0 1.5rem',
                                            height: '36px',
                                            minWidth: '120px'
                                        }}
                                    >
                                        <span
                                            className="text-sm font-black text-white line-through decoration-white/60 drop-shadow-md whitespace-nowrap"
                                            style={{ color: '#FFFFFF' }}
                                        >
                                            {formatValue(notif.oldValue, notif.type)}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-center">
                                        <span className="material-icons opacity-70 text-lg font-black scale-125" style={{ color: 'var(--text-primary)' }}>east</span>
                                    </div>

                                    {/* Comparison View (New Value) */}
                                    <div
                                        className="flex items-center justify-center border border-white/20"
                                        style={{
                                            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                            boxShadow: '0 0 15px rgba(34, 197, 94, 0.6), inset 0 2px 4px rgba(255,255,255,0.2)',
                                            borderRadius: '50px',
                                            padding: '0 1.5rem',
                                            height: '36px',
                                            minWidth: '120px'
                                        }}
                                    >
                                        <span
                                            className="text-sm font-black text-white drop-shadow-md whitespace-nowrap"
                                            style={{ color: '#FFFFFF' }}
                                        >
                                            {formatValue(notif.newValue, notif.type)}
                                        </span>
                                    </div>

                                    {/* Performer Name */}
                                    <div className="flex items-center gap-2">
                                        <span className="material-icons opacity-40 text-sm">person</span>
                                        <span className="text-[11px] font-black uppercase tracking-tight" style={{ color: 'var(--text-secondary)' }}>
                                            {notif.performedBy.name?.split(' ')[0]} {notif.performedBy.name?.split(' ')[1]?.charAt(0)}.
                                        </span>
                                    </div>

                                    {/* Company Info */}
                                    <div className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-lg border border-white/10">
                                        <span className="material-icons opacity-40 text-[12px]">business</span>
                                        <span className="text-[10px] font-black uppercase truncate max-w-[140px]" style={{ color: 'var(--text-light)' }}>
                                            {notif.performedBy.companyName || 'SİSTEM'}
                                        </span>
                                    </div>

                                    {/* Action Button */}
                                    <div className="flex justify-end">
                                        <div
                                            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 ${notif.read
                                                ? 'bg-white/5 text-white/20 border border-white/10'
                                                : 'bg-primary text-white shadow-[0_4px_15px_rgba(59,130,246,0.3)] hover:scale-110'
                                                }`}
                                        >
                                            <span className="material-icons text-xl">{notif.read ? 'arrow_forward' : 'chevron_right'}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Unread Indication Glow */}
                                {!notif.read && (
                                    <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-12 bg-primary rounded-full blur-[4px] opacity-60" />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* History Modal */}
            <HistoryLogModal
                isOpen={isHistoryOpen}
                onClose={() => setIsHistoryOpen(false)}
                entityId={selectedReservationId}
                tenantId={userData?.tenantId}
                title={`${filteredNotifications.find(n => n.reservationId === selectedReservationId)?.plateNumber || ''} - Geçmiş Hareketler`}
            />

            {/* Custom Styles for this page */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: var(--border-color);
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.2);
                }
                .animate-fadeIn {
                    animation: fadeIn 0.5s ease-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes pulse-white {
                    0% { box-shadow: 0 0 5px white, 0 0 0px white; }
                    50% { box-shadow: 0 0 15px white, 0 0 10px white; }
                    100% { box-shadow: 0 0 5px white, 0 0 0px white; }
                }
                .text-shadow-md {
                    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
                }
            `}</style>
        </div>
    );
};

export default Notifications;
